import React, { useEffect } from "react";
import styles from "./styles.module.scss";
import { Input, CustomSelect } from "../form";
import { HiringAssistantContinuePayload, useTimelineUpdate } from "hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from 'moment-timezone';
import { isTalentAudit } from "helpers/assistantFlow";

interface TimelineFormData {
  timeZone: { value: string; label: string };
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

const timeZones = moment.tz.names().map((zone) => ({
  value: zone,
  label: `${zone} (UTC${moment.tz(zone).format('Z')})`,
}));

// Every flow defaults to Lagos rather than the first zone alphabetically
// (Africa/Abidjan), which recruiters kept having to correct.
const DEFAULT_TIME_ZONE = 'Africa/Lagos';
const defaultTimeZone =
  timeZones.find((zone) => zone.value === DEFAULT_TIME_ZONE) ?? timeZones[0];

const validationSchema = Yup.object().shape({
  timeZone: Yup.object().shape({
    value: Yup.string().required("Time zone is required"),
    label: Yup.string().required("Time zone is required"),
  }).required("Time zone is required"),
  startDate: Yup.string()
    .required("Start date is required")
    .test("is-future-date", "Start date must be today or later", (value) => {
      return moment(value).isSameOrAfter(moment(), 'day');
    }),
  startTime: Yup.string().required("Start time is required"),
  endDate: Yup.string()
    .required("End date is required")
    .test("is-after-start", "End date must be after start date", function (value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return true;
      return moment(value).isSameOrAfter(moment(startDate), 'day');
    }),
  endTime: Yup.string().required("End time is required"),
});

interface TimelineProps {
  assessmentId: string | null;
  isSubmitted?: boolean;
  sendRequest: (payload: HiringAssistantContinuePayload) => void;
  hasJobAssessment?: boolean;
}

const TimelineComponent: React.FC<TimelineProps> = ({ assessmentId, isSubmitted, sendRequest, hasJobAssessment }) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<TimelineFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      timeZone: defaultTimeZone,
      startDate: moment().format('YYYY-MM-DD'),
      startTime: moment().format('HH:mm'),
      endDate: moment().format('YYYY-MM-DD'),
      endTime: "23:59",
    },
  });

  const { submitTimelineUpdate, loading, isSuccess, reset } = useTimelineUpdate();

  useEffect(() => {
    if (isSuccess) {
      if (!hasJobAssessment) {
        // The talent audit advances past the timeline with its own input; `continue`
        // is not a recognised transition for that flow's state machine.
        sendRequest({
          assessment_id: assessmentId,
          input: isTalentAudit() ? 'continue_timeline' : 'continue',
        });
      }
      reset();
    }
  }, [isSuccess]);

  const onSubmit = async (data: TimelineFormData) => {
    try {
      const startDateTime = moment.tz(
        `${data.startDate}T${data.startTime}`,
        data.timeZone.value
      ).format();
      const endDateTime = moment.tz(
        `${data.endDate}T${data.endTime}`,
        data.timeZone.value
      ).format();

      const payload = {
        start_date: startDateTime,
        end_date: endDateTime,
        time_zone: data.timeZone,
      };

      await submitTimelineUpdate(assessmentId, payload);
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  return (
    <div className={styles.timelineContainer}>
      <h2>{isTalentAudit() ? 'Set Assessment Timeline' : 'Set Application Timeline'}</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CustomSelect
          name="timeZone"
          placeholder="Select time zone"
          options={timeZones}
          value={watch("timeZone")}
          onChange={(val) => setValue("timeZone", val)}
          validatorMessage={errors.timeZone?.value?.message || ""}
          label="Time zone"
          showRequired
          parentClassName={styles.selectField}
          inputClass={styles.selectInput}

        />
        <div className={styles.dateTimeContainer}>
          <div className={styles.dateTimeGroup}>
            <Input
              placeholder=""
              type="date"
              name="startDate"
              register={register}
              validatorMessage={errors.startDate?.message || ""}
              label="Date & Time"
              showRequired
              parentClassName={styles.dateInputWrapper}
              className={styles.dateInput}
              min={moment().format('YYYY-MM-DD')}
            />
            <Input
              placeholder=""
              type="time"
              name="startTime"
              register={register}
              validatorMessage={errors.startTime?.message || ""}
              showRequired
              parentClassName={styles.dateInputWrapper}
              className={styles.dateInput}
            />
          </div>
          <span>to</span>
          <div className={styles.dateTimeGroup}>
            <Input
              placeholder=""
              type="date"
              name="endDate"
              register={register}
              validatorMessage={errors.endDate?.message || ""}
              showRequired
              parentClassName={styles.dateInputWrapper}
              className={styles.dateInput}
              min={watch("startDate") || moment().format('YYYY-MM-DD')}
            />
            <Input
              placeholder=""
              type="time"
              name="endTime"
              register={register}
              validatorMessage={errors.endTime?.message || ""}
              showRequired
              parentClassName={styles.dateInputWrapper}
              className={styles.dateInput}
            />
          </div>
        </div>
        {isSubmitted &&
          <button type="submit" disabled={loading} className={styles.submit}>
            {loading ? "Submitting..." : "Done"}
          </button>
        }
      </form>
    </div>
  );
};

export default TimelineComponent;